/**
 * CAROL — Multi-tenant AI Hostess Widget
 *
 * Features:
 * - Visitor behavior tracking (page_view, product_view, add_to_cart, idle, exit_intent)
 * - Proactive engagement (decision engine polling)
 * - Customer identification by phone
 * - Real-time catalog (server-validated actions)
 * - Multi-tenant: adapts to each restaurant dynamically
 */
import { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Send, X, Loader2, User,
  ShoppingCart, Plus, Phone, Sparkles,
} from "lucide-react";
import axios from "axios";
import { API_URL } from "@/utils/apiConfig";
import { toBaseLanguage } from "@/i18n";

const FAB_BUBBLE = {
  pt: "Posso ajudar com o seu pedido? 😊",
  es: "¿Puedo ayudarte con tu pedido? 😊",
  en: "Can I help with your order? 😊",
};

function resolveCarolLang(langProp) {
  if (langProp) {
    const base = toBaseLanguage(langProp);
    if (base === "pt" || base === "es" || base === "en") return base;
  }
  if (typeof navigator !== "undefined") {
    if (navigator.language?.startsWith("pt")) return "pt";
    if (navigator.language?.startsWith("en")) return "en";
  }
  return "es";
}

const _BACKEND_URL = API_URL || "";
const _isProxied = (() => {
  try {
    const backendHost = new URL(_BACKEND_URL).hostname;
    const currentHost = window.location.hostname;
    return currentHost !== backendHost && !currentHost.includes("localhost");
  } catch { return false; }
})();
const API = _isProxied ? "/api" : `${_BACKEND_URL}/api`;

function generateSessionId() {
  return "carol_" + Math.random().toString(36).slice(2, 15) + Date.now().toString(36);
}

// ── ADD button parser (same format as before + Carol's ADD_ITEM actions) ──────
const ADD_REGEX = /\[ADD:([^:]+):([^:]+):([^\]]+)\]/g;

function parseMessage(text) {
  const parts = [];
  let lastIndex = 0;
  let match;
  const regex = new RegExp(ADD_REGEX.source, "g");
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex)
      parts.push({ type: "text", content: text.slice(lastIndex, match.index) });
    parts.push({ type: "add", id: match[1], name: match[2], price: parseFloat(match[3]) });
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < text.length)
    parts.push({ type: "text", content: text.slice(lastIndex) });
  return parts;
}

function MessageContent({ text, accentColor, onAddToCart, addedItems }) {
  const parts = parseMessage(text);
  return (
    <div className="space-y-1">
      {parts.map((part, i) => {
        if (part.type === "text") return <span key={i}>{part.content}</span>;
        const isAdded = addedItems.has(part.id);
        return (
          <button
            key={i}
            onClick={() => !isAdded && onAddToCart(part.id, part.name, part.price)}
            disabled={isAdded}
            className={`flex items-center gap-1.5 w-full mt-1.5 px-3 py-2 rounded-xl text-[12px] font-bold transition-all ${
              isAdded
                ? "bg-gray-100 text-gray-400 border border-gray-200"
                : "text-white border-0 shadow-sm hover:opacity-90 active:scale-[0.98]"
            }`}
            style={!isAdded ? { backgroundColor: accentColor } : {}}
          >
            {isAdded ? (
              <><ShoppingCart className="w-3.5 h-3.5" /><span className="flex-1 text-left truncate">{part.name}</span><span>✓</span></>
            ) : (
              <><Plus className="w-3.5 h-3.5" /><span className="flex-1 text-left truncate">{part.name}</span><span>{part.price.toFixed(2)}</span></>
            )}
          </button>
        );
      })}
    </div>
  );
}

// ── Phone identification form ─────────────────────────────────────────────────
function PhoneForm({ accentColor, lang, onIdentify }) {
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  const labels = {
    pt: { title: "Para personalizar seu atendimento", phonePh: "Seu telefone", namePh: "Seu nome (opcional)", btn: "Confirmar" },
    es: { title: "Para personalizar tu atención", phonePh: "Tu teléfono", namePh: "Tu nombre (opcional)", btn: "Confirmar" },
    en: { title: "To personalize your experience", phonePh: "Your phone", namePh: "Your name (optional)", btn: "Confirm" },
  };
  const L = labels[lang] || labels.es;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!phone.trim() || phone.trim().length < 7) return;
    setLoading(true);
    await onIdentify(phone.trim(), name.trim());
    setLoading(false);
  };

  return (
    <form onSubmit={handleSubmit} className="bg-blue-50 border border-blue-100 rounded-xl p-3 mx-1 my-1 space-y-2">
      <p className="text-[11px] text-blue-700 font-medium">{L.title}</p>
      <Input
        value={phone}
        onChange={e => setPhone(e.target.value)}
        placeholder={L.phonePh}
        className="h-8 text-sm rounded-lg"
        type="tel"
        inputMode="tel"
      />
      <Input
        value={name}
        onChange={e => setName(e.target.value)}
        placeholder={L.namePh}
        className="h-8 text-sm rounded-lg"
      />
      <Button
        type="submit"
        disabled={loading || phone.trim().length < 7}
        className="w-full h-8 text-sm rounded-lg text-white"
        style={{ backgroundColor: accentColor }}
      >
        {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : L.btn}
      </Button>
    </form>
  );
}

// ── Main Widget ───────────────────────────────────────────────────────────────
export default function CarolWidget({
  restaurantId,
  restaurantName,
  botPhotoUrl = null,
  accentColor = "#059669",
  lang: langProp,
  onAddToCart,
  onRemoveFromCart,
  // Visitor tracking props (passed from parent page)
  currentProductId = null,
  currentProductName = null,
  currentProductPrice = null,
  cartItems = [],
}) {
  const lang = resolveCarolLang(langProp);

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => {
    const key = `carol_session_${restaurantId}`;
    const stored = sessionStorage.getItem(key);
    if (stored) return stored;
    const newId = generateSessionId();
    sessionStorage.setItem(key, newId);
    return newId;
  });
  const [addedItems, setAddedItems] = useState(new Set());
  const [showPhoneForm, setShowPhoneForm] = useState(false);
  const [isIdentified, setIsIdentified] = useState(false);
  const [proactivePulse, setProactivePulse] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [customerPhone, setCustomerPhone] = useState(null);
  const [customerName, setCustomerName] = useState(null);
  const [resolvedPhotoUrl, setResolvedPhotoUrl] = useState(botPhotoUrl);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const triggerPollerRef = useRef(null);
  const idleTimerRef = useRef(null);
  const sessionStartRef = useRef(Date.now());
  const paymentPollRef = useRef(null); // interval for polling payment confirmation

  const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });

  // ── API helpers ─────────────────────────────────────────────────────────────
  const carolAPI = useCallback(async (path, data = null, method = "POST") => {
    const url = `${API}/carol/${restaurantId}${path}`;
    try {
      const res = method === "GET"
        ? await axios.get(url, { params: data, timeout: 8000 })
        : await axios.post(url, data, { timeout: 12000 });
      return res.data;
    } catch (err) {
      console.warn("[Carol]", path, err?.message);
      return null;
    }
  }, [restaurantId]);

  const trackEvent = useCallback((eventType, metadata = {}) => {
    carolAPI("/event", { session_id: sessionId, event_type: eventType, metadata });
  }, [carolAPI, sessionId]);

  // ── Payment confirmation polling ─────────────────────────────────────────
  // Called after Carol sends an online payment link. Polls every 3s until
  // the Viva/Stripe webhook confirms payment (order status → "pending").
  const onClearCart = useCallback(() => {
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("carol:clear_cart"));
    }
  }, []);

  const startPaymentPolling = useCallback((orderId, orderNumber, total) => {
    if (paymentPollRef.current) clearInterval(paymentPollRef.current);
    let attempts = 0;
    const MAX_ATTEMPTS = 200; // ~10 minutes at 3s intervals
    paymentPollRef.current = setInterval(async () => {
      attempts++;
      if (attempts > MAX_ATTEMPTS) {
        clearInterval(paymentPollRef.current);
        paymentPollRef.current = null;
        return;
      }
      try {
        const result = await carolAPI(
          `/order-payment-status?order_id=${orderId}&session_id=${sessionId}`,
          null, "GET"
        );
        if (result?.confirmed) {
          clearInterval(paymentPollRef.current);
          paymentPollRef.current = null;
          const num = orderNumber || orderId.slice(0, 8).toUpperCase();
          const sym = "€";
          const confirmMsg = lang === "pt"
            ? `✅ *Pagamento confirmado!* Recebemos o teu pedido *#${num}* e já estamos a preparar. Avisamos quando estiver pronto! 🎉`
            : lang === "en"
            ? `✅ *Payment confirmed!* We received your order *#${num}* and are already preparing it. We'll let you know when it's ready! 🎉`
            : `✅ *¡Pago confirmado!* Recibimos tu pedido *#${num}* y ya lo estamos preparando. ¡Te avisamos cuando esté listo! 🎉`;
          setMessages(prev => [...prev, { role: "assistant", text: confirmMsg }]);
          onClearCart();
        }
      } catch { /* ignore poll errors */ }
    }, 3000);
  }, [carolAPI, sessionId, lang, onClearCart]);

  // ── Fetch carol config (photo, name) from status endpoint on mount ───────
  useEffect(() => {
    if (!restaurantId) return;
    carolAPI("/status", null, "GET").then(data => {
      if (data?.carol_photo_url) setResolvedPhotoUrl(data.carol_photo_url);
    });
  }, [restaurantId]); // eslint-disable-line

  // ── Track page_view on mount ─────────────────────────────────────────────
  useEffect(() => {
    trackEvent("page_view", {
      url: window.location.href,
      referrer: document.referrer,
      restaurant_id: restaurantId,
    });
  }, []);  // eslint-disable-line

  // ── Track product_view when currentProductId changes ────────────────────
  useEffect(() => {
    if (currentProductId) {
      trackEvent("product_view", {
        product_id: currentProductId,
        product_name: currentProductName,
        price: currentProductPrice,
      });
    }
  }, [currentProductId]);  // eslint-disable-line

  // ── Track add_to_cart when cartItems changes ─────────────────────────────
  const prevCartRef = useRef([]);
  useEffect(() => {
    const prev = prevCartRef.current;
    cartItems.forEach(item => {
      const prevItem = prev.find(p => p.id === item.id || p.cartId === item.id);
      const qty = item.quantity || 1;
      const prevQty = prevItem ? (prevItem.quantity || 1) : 0;
      if (qty > prevQty) {
        trackEvent("add_to_cart", {
          product_id: item.id || item.cartId,
          product_name: item.name,
          price: item.price,
          quantity: qty - prevQty,
        });
      }
    });
    prevCartRef.current = cartItems;
  }, [cartItems]);  // eslint-disable-line

  // ── Idle timer ───────────────────────────────────────────────────────────
  useEffect(() => {
    const resetIdle = () => {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => {
        trackEvent("idle", { idle_seconds: 45 });
      }, 45000);
    };
    window.addEventListener("mousemove", resetIdle);
    window.addEventListener("keydown", resetIdle);
    window.addEventListener("scroll", resetIdle);
    window.addEventListener("touchstart", resetIdle);
    resetIdle();
    return () => {
      clearTimeout(idleTimerRef.current);
      window.removeEventListener("mousemove", resetIdle);
      window.removeEventListener("keydown", resetIdle);
      window.removeEventListener("scroll", resetIdle);
      window.removeEventListener("touchstart", resetIdle);
    };
  }, []);  // eslint-disable-line

  // ── Exit intent ──────────────────────────────────────────────────────────
  useEffect(() => {
    const handleMouseLeave = async (e) => {
      if (e.clientY <= 0 && !open) {
        trackEvent("exit_intent", {});
        const result = await carolAPI("/exit-intent", null, "POST");
        if (result?.trigger?.message) {
          addProactiveMessage(result.trigger.message);
        }
      }
    };
    document.addEventListener("mouseleave", handleMouseLeave);
    return () => document.removeEventListener("mouseleave", handleMouseLeave);
  }, [open]);  // eslint-disable-line

  // ── Proactive trigger polling ─────────────────────────────────────────────
  useEffect(() => {
    let pollCount = 0;
    triggerPollerRef.current = setInterval(async () => {
      pollCount++;
      // Stop polling after 5min (30 polls × 10s) or when chat is open
      if (pollCount > 30 || open) return;

      const result = await carolAPI("/trigger", { session_id: sessionId }, "GET");
      if (result?.trigger?.message) {
        addProactiveMessage(result.trigger.message);
      }
    }, 10000);

    return () => {
      clearInterval(triggerPollerRef.current);
      if (paymentPollRef.current) clearInterval(paymentPollRef.current);
    };
  }, [open]);  // eslint-disable-line

  const addProactiveMessage = (text) => {
    setMessages(prev => {
      // Don't add duplicate proactive messages
      if (prev.some(m => m.text === text)) return prev;
      return [...prev, { role: "assistant", text, proactive: true }];
    });
    setProactivePulse(true);
    setUnreadCount(c => c + 1);
    setTimeout(() => setProactivePulse(false), 3000);
  };

  // ── Chat open/close ─────────────────────────────────────────────────────
  const handleOpen = () => {
    setOpen(true);
    setUnreadCount(0);
    setProactivePulse(false);

    if (messages.length === 0) {
      const greetings = {
        es: `¡Hola! Soy Carol, la asistente de ${restaurantName || "este restaurante"}. ¿En qué puedo ayudarte hoy? 😊`,
        pt: `Olá! Sou a Carol, atendente do ${restaurantName || "restaurante"}. Como posso ajudar? 😊`,
        en: `Hi! I'm Carol, assistant for ${restaurantName || "this restaurant"}. How can I help you today? 😊`,
      };
      setMessages([{ role: "assistant", text: greetings[lang] || greetings.es }]);
    }
  };

  // ── Send message ──────────────────────────────────────────────────────────
  const handleSend = async () => {
    const text = input.trim();
    if (!text || loading) return;

    setMessages(prev => [...prev, { role: "user", text }]);
    setInput("");
    setLoading(true);

    const cartPayload = (cartItems || []).map(i => ({
      id: i.id || i.cartId,
      name: i.name,
      price: i.price,
      quantity: i.quantity || 1,
    }));

    const data = await carolAPI("/message", {
      session_id: sessionId,
      message: text,
      customer_phone: customerPhone,
      customer_name: customerName,
      cart: cartPayload.length > 0 ? cartPayload : null,
    });

    if (data?.message) {
      setMessages(prev => [...prev, { role: "assistant", text: data.message }]);

      // Process server-validated actions
      if (data.actions?.length) {
        processActions(data.actions);
      }
    } else {
      const err = { pt: "Desculpe, tive um problema. Pode repetir?", es: "Lo siento, tuve un problema. ¿Puedes repetir?" };
      setMessages(prev => [...prev, { role: "assistant", text: err[lang] || err.es }]);
    }
    setLoading(false);
  };

  // ── Process Carol's actions ───────────────────────────────────────────────
  const processActions = async (actions) => {
    actions.forEach(async action => {
      if (action.type === "ADD_ITEM" && onAddToCart) {
        onAddToCart({
          id: action.product_id,
          name: action.product_name,
          price: action.unit_price,
        });
        setAddedItems(prev => new Set(prev).add(action.product_id));
      } else if (action.type === "REMOVE_ITEM" && onRemoveFromCart) {
        onRemoveFromCart(action.product_id);
        setAddedItems(prev => { const s = new Set(prev); s.delete(action.product_id); return s; });
      } else if (action.type === "REQUEST_PHONE") {
        setShowPhoneForm(true);
      } else if (action.type === "OPEN_WHATSAPP") {
        // Will be handled by parent or direct link
        const waMsgs = { pt: "📱 Clique para falar no WhatsApp", es: "📱 Haz clic para hablar por WhatsApp" };
        setMessages(prev => [...prev, { role: "assistant", text: waMsgs[lang] || waMsgs.es, isWhatsApp: true }]);
      } else if (action.type === "OPEN_PAYMENT_LINK") {
        // Payment link was embedded in the chat message — now poll for confirmation.
        // When Viva/Stripe webhook fires, order status becomes "pending" → show auto-confirmation.
        if (action.order_id) {
          startPaymentPolling(action.order_id, action.order_number, action.total);
        }
      } else if (action.type === "CREATE_ORDER") {
        // Carol creates the order directly → goes to KDS immediately
        const cartPayload = (cartItems || []).map(i => ({
          id: i.id || i.cartId,
          name: i.name,
          price: i.price,
          quantity: i.quantity || 1,
        }));
        try {
          const orderResult = await carolAPI("/order", {
            session_id: sessionId,
            order_type: action.order_type || "pickup",
            payment_method: action.payment_method || "cash",
            delivery_address: action.delivery_address || "",
            table_number: action.table_number || null,
            notes: action.notes || "",
            customer_name: customerName,
            customer_phone: customerPhone,
            use_points: action.use_points || false,
            cart: cartPayload,
          });
          if (orderResult?.order_id) {
            const sym = action.currency_symbol || "€";
            const num = orderResult.order_number || orderResult.order_id.slice(0, 8).toUpperCase();
            const confirmMsg = lang === "pt"
              ? `✅ Pedido #${num} enviado para a cozinha! Total: ${sym}${orderResult.total?.toFixed(2)} · Tempo estimado: ${orderResult.wait_time_min}-${orderResult.wait_time_max}min`
              : `✅ Pedido #${num} enviado a cocina! Total: ${sym}${orderResult.total?.toFixed(2)} · Tiempo estimado: ${orderResult.wait_time_min}-${orderResult.wait_time_max}min`;
            setMessages(prev => [...prev, { role: "assistant", text: confirmMsg }]);
            if (onClearCart) onClearCart();
          }
        } catch (err) {
          const errMsgs = { pt: "Não consegui criar o pedido. Por favor tenta pelo checkout normal.", es: "No pude crear el pedido. Intenta por el checkout normal." };
          setMessages(prev => [...prev, { role: "assistant", text: errMsgs[lang] || errMsgs.es }]);
        }
      }
    });
  };

  // ── Add item from [ADD:...] buttons ────────────────────────────────────────
  const handleAddItem = (productId, productName, price) => {
    if (onAddToCart) onAddToCart({ id: productId, name: productName, price });
    setAddedItems(prev => new Set(prev).add(productId));
    trackEvent("add_to_cart", { product_id: productId, product_name: productName, price, quantity: 1 });
  };

  // ── Customer identification ───────────────────────────────────────────────
  const handleIdentify = async (phone, name) => {
    const result = await carolAPI("/identify", { session_id: sessionId, phone, name });
    if (result?.success) {
      setCustomerPhone(phone);
      setCustomerName(name || result.name || "");
      setIsIdentified(true);
      setShowPhoneForm(false);
      if (result.welcome_message) {
        setMessages(prev => [...prev, { role: "assistant", text: result.welcome_message }]);
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  useEffect(() => { scrollToBottom(); }, [messages]);
  useEffect(() => { if (open && inputRef.current) inputRef.current.focus(); }, [open]);

  // ── Render ─────────────────────────────────────────────────────────────────
  const bubbleText = FAB_BUBBLE[lang] || FAB_BUBBLE.es;

  const fabButton = (
    <div className={`fixed right-4 z-[999] flex flex-col items-end gap-2 transition-all duration-200 ${cartItems?.length > 0 ? "bottom-[72px] lg:bottom-6" : "bottom-6"}`} data-testid="carol-fab-wrapper">
      {/* Thought bubble — always mounted, shown/hidden via CSS to avoid React remount issues */}
      <div
        className="relative mr-2"
        style={{
          animation: "carolBubble 3s ease-in-out infinite",
          opacity: open ? 0 : 1,
          visibility: open ? "hidden" : "visible",
          transition: "opacity 0.2s ease",
          pointerEvents: open ? "none" : "auto",
        }}
      >
        <div className="bg-white text-gray-700 text-[12px] font-semibold px-3 py-2 rounded-2xl shadow-md border border-gray-100 whitespace-nowrap">
          {bubbleText}
        </div>
        {/* Bubble tail pointing down-right */}
        <div className="absolute -bottom-2 right-5 w-0 h-0"
          style={{ borderLeft: "6px solid transparent", borderRight: "6px solid transparent", borderTop: "8px solid white", filter: "drop-shadow(0 1px 0 #e5e7eb)" }}
        />
      </div>

      {/* Avatar button */}
      <button
        onClick={handleOpen}
        className={`relative w-14 h-14 rounded-full shadow-xl flex items-center justify-center transition-transform hover:scale-110 active:scale-95 ${proactivePulse ? "animate-bounce" : ""}`}
        style={{ backgroundColor: accentColor, padding: 0 }}
        data-testid="carol-fab"
      >
        {resolvedPhotoUrl ? (
          <img src={resolvedPhotoUrl} alt="Carol" className="w-14 h-14 rounded-full object-cover" />
        ) : (
          /* Carol face avatar */
          <svg viewBox="0 0 56 56" className="w-14 h-14" fill="none" xmlns="http://www.w3.org/2000/svg">
            {/* Head */}
            <circle cx="28" cy="28" r="28" fill={accentColor} />
            {/* Hair */}
            <ellipse cx="28" cy="14" rx="14" ry="8" fill="rgba(0,0,0,0.35)" />
            <ellipse cx="14" cy="20" rx="5" ry="10" fill="rgba(0,0,0,0.35)" />
            <ellipse cx="42" cy="20" rx="5" ry="10" fill="rgba(0,0,0,0.35)" />
            {/* Face */}
            <circle cx="28" cy="30" r="14" fill="#FDDBB4" />
            {/* Eyes */}
            <circle cx="23" cy="27" r="2" fill="#333" />
            <circle cx="33" cy="27" r="2" fill="#333" />
            <circle cx="24" cy="26" r="0.7" fill="white" />
            <circle cx="34" cy="26" r="0.7" fill="white" />
            {/* Smile */}
            <path d="M22 32 Q28 37 34 32" stroke="#c0636a" strokeWidth="1.5" strokeLinecap="round" fill="none" />
            {/* Cheeks */}
            <ellipse cx="20" cy="31" rx="3" ry="2" fill="rgba(255,150,150,0.4)" />
            <ellipse cx="36" cy="31" rx="3" ry="2" fill="rgba(255,150,150,0.4)" />
          </svg>
        )}
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center shadow">
            {unreadCount}
          </span>
        )}
      </button>

    </div>
  );

  const chatPanel = (
    <div
      className="fixed bottom-24 right-4 z-[999] w-[340px] max-w-[calc(100vw-2rem)] h-[480px] max-h-[80vh] flex flex-col rounded-2xl shadow-2xl overflow-hidden border border-gray-100"
      data-testid="carol-panel"
    >
      {/* Header */}
      <div
        className="flex items-center gap-3 px-4 py-3 shrink-0"
        style={{ backgroundColor: accentColor }}
      >
        <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center shrink-0">
          <Sparkles className="w-4.5 h-4.5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-white truncate">Carol</p>
          <p className="text-[10px] text-white/80">{restaurantName || "Assistente"}{isIdentified ? " · ✓ Identificado" : ""}</p>
        </div>
        <button
          onClick={() => setOpen(false)}
          className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
          data-testid="carol-close"
        >
          <X className="w-4 h-4 text-white" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3 bg-gray-50">
        {messages.map((msg, i) => (
          <div
            key={i}
            className={`flex gap-2 ${msg.role === "user" ? "justify-end" : "justify-start"}`}
            data-testid={`carol-msg-${i}`}
          >
            {msg.role === "assistant" && (
              <div
                className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5"
                style={{ backgroundColor: `${accentColor}20` }}
              >
                <Sparkles className="w-3.5 h-3.5" style={{ color: accentColor }} />
              </div>
            )}
            <div
              className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-[13px] leading-relaxed ${
                msg.role === "user"
                  ? "text-white rounded-br-md"
                  : "bg-white text-gray-800 border border-gray-100 rounded-bl-md shadow-sm"
              } ${msg.proactive ? "ring-2 ring-offset-1" : ""}`}
              style={msg.role === "user" ? { backgroundColor: accentColor } : msg.proactive ? { ringColor: accentColor } : {}}
            >
              {msg.role === "assistant" ? (
                <MessageContent
                  text={msg.text}
                  accentColor={accentColor}
                  onAddToCart={handleAddItem}
                  addedItems={addedItems}
                />
              ) : msg.text}
            </div>
            {msg.role === "user" && (
              <div className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center shrink-0 mt-0.5">
                <User className="w-3.5 h-3.5 text-gray-500" />
              </div>
            )}
          </div>
        ))}

        {/* Phone identification form */}
        {showPhoneForm && !isIdentified && (
          <PhoneForm accentColor={accentColor} lang={lang} onIdentify={handleIdentify} />
        )}

        {loading && (
          <div className="flex gap-2 items-center" data-testid="carol-loading">
            <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ backgroundColor: `${accentColor}20` }}>
              <Sparkles className="w-3.5 h-3.5" style={{ color: accentColor }} />
            </div>
            <div className="bg-white border border-gray-100 rounded-2xl rounded-bl-md px-4 py-3 shadow-sm">
              <div className="flex gap-1">
                {[0, 150, 300].map(delay => (
                  <span key={delay} className="w-2 h-2 rounded-full bg-gray-300 animate-bounce" style={{ animationDelay: `${delay}ms` }} />
                ))}
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Identify button (if not yet identified) */}
      {!isIdentified && !showPhoneForm && messages.length > 2 && (
        <div className="px-3 py-1.5 bg-gray-50 border-t border-gray-100 shrink-0">
          <button
            onClick={() => setShowPhoneForm(true)}
            className="flex items-center gap-1.5 text-[11px] text-gray-500 hover:text-gray-700 transition-colors"
          >
            <Phone className="w-3 h-3" />
            {lang === "pt" ? "Identificar-me para atendimento personalizado" : "Identificarme para atención personalizada"}
          </button>
        </div>
      )}

      {/* Input */}
      <div className="px-3 py-2.5 border-t bg-white shrink-0">
        <div className="flex gap-2">
          <Input
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={lang === "pt" ? "Pergunte sobre o cardápio..." : "Pregunta sobre el menú..."}
            className="h-10 text-sm rounded-xl border-gray-200 focus-visible:ring-1"
            style={{ "--tw-ring-color": accentColor }}
            disabled={loading}
            data-testid="carol-input"
          />
          <Button
            onClick={handleSend}
            disabled={!input.trim() || loading}
            className="h-10 w-10 rounded-xl shrink-0 p-0 text-white"
            style={{ backgroundColor: accentColor }}
            data-testid="carol-send"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </div>
      </div>
    </div>
  );

  return createPortal(
    <>
      <style>{`
        @keyframes carolBubble {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-4px); }
        }
      `}</style>
      {fabButton}
      {open && chatPanel}
    </>,
    document.body
  );
}
